<template>
    <div class="preview-container">
        <AppHeader title="Device Preview" :back-to="backRoute" back-text="Back to Devices"/>

        <main class="main-content">
            <div v-if="device" class="device-preview">
                <img :src="editableDevice.imageCdnPath || '/placeholder-phone.svg'" :alt="editableDevice.model"
                     class="device-image" @error="handleImageError">
                <div class="device-info">
                    <div class="form-group">
                        <label>Model:</label>
                        <input v-model="editableDevice.model" type="text"/>
                    </div>
                    <div class="form-group">
                        <label>ID:</label>
                        <input v-model="editableDevice.id" type="text" disabled/>
                    </div>
                    <div class="form-group" v-if="userRole === 'admin'">
                        <label>PIN:</label>
                        <input v-model="editableDevice.pin" type="text"/>
                    </div>
                    <div class="form-group">
                        <label>Image URL:</label>
                        <input v-model="editableDevice.imageCdnPath" type="text"/>
                    </div>
                    <div class="form-group">
                        <label>Type:</label>
                        <select v-model="editableDevice.type">
                            <option value="smartphone">Smartphone</option>
                            <option value="tablet">Tablet</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>Model ID:</label>
                        <input v-model="editableDevice.modelId" type="text"/>
                    </div>
                    <div class="form-group">
                        <label>OS:</label>
                        <select v-model="editableDevice.os">
                            <option value="iOS">iOS</option>
                            <option value="Android">Android</option>
                            <option value="iPadOS">iPadOS</option>
                            <option value="Windows">Windows</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>OS Version:</label>
                        <input v-model="editableDevice.osVersion" type="text"/>
                    </div>
                    <div class="form-group ram-field">
                        <label class="ram-label">RAM (GB):</label>
                        <input v-model="editableDevice.ram" type="number" step="1" class="ram-input"/>
                    </div>
                    <div class="form-group">
                        <label>Team:</label>
                        <select v-model="editableDevice.team">
                            <option value="" disabled>Select Team</option>
                            <option value="Top Eleven">Top Eleven</option>
                            <option value="Golf Rival">Golf Rival</option>
                            <option value="Top Goal">Top Goal</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>Owner:</label>
                        <select v-model="editableDevice.owner">
                            <option value="" disabled>Select Owner</option>
                            <option v-for="admin in adminUsers" :key="admin.email" :value="admin.email">
                                {{ admin.name || admin.email }}
                            </option>
                        </select>
                    </div>
                    <div class="form-group notes-field">
                        <label>Notes:</label>
                        <textarea v-model="editableDevice.notes" rows="3" placeholder="Additional information about this device..."></textarea>
                    </div>
                    <div class="form-group">
                        <label>Last Taken By:</label>
                        <input
                            v-model="editableDevice.lastTakenBy"
                            type="text"
                            :disabled="!canEditLastTakenBy"
                        />
                        <button
                            v-if="canSignOff"
                            @click="signOffDevice"
                            class="sign-off-btn"
                        >
                            Sign Off Device
                        </button>
                    </div>
                    <div class="form-group" v-if="editableDevice.lastTakenAt">
                        <label>Last Taken At:</label>
                        <input :value="formatDate(editableDevice.lastTakenAt)" type="text" disabled/>
                    </div>
                    <div class="form-group">
                        <label>Total Usage Hours:</label>
                        <input :value="(editableDevice.totalHours || 0).toFixed(1)" type="text" disabled/>
                    </div>
                    <div class="form-group">
                        <label>Total Checkouts:</label>
                        <input :value="editableDevice.checkoutCount || 0" type="text" disabled/>
                    </div>
                    <div class="form-group" v-if="currentUsageHours > 0">
                        <label>Current Session Hours:</label>
                        <input :value="currentUsageHours.toFixed(1)" type="text" disabled/>
                    </div>
                    <div class="history-section" v-if="userRole === 'admin' && editHistory.length > 0">
                        <div class="history-header-row">
                            <label>Edit History:</label>
                            <button @click="showHistory = !showHistory" class="toggle-history-btn">
                                {{ showHistory ? 'Hide History' : 'Show History' }}
                            </button>
                        </div>
                        <div class="history-list" v-if="showHistory">
                            <div v-for="entry in editHistory" :key="entry.id" class="history-entry">
                                <div class="history-header">
                                    <span class="history-user">{{ entry.editedBy }}</span>
                                    <span class="history-date">{{ formatDate(entry.editedAt) }}</span>
                                </div>
                                <div class="history-changes">
                                    <div v-for="(change, field) in entry.changes" :key="field" class="change-item">
                                        <strong>{{ field }}:</strong> {{ change.old }} → {{ change.new }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="qr-section">
                        <label>QR Code:</label>
                        <div class="qr-container">
                            <canvas ref="qrCanvas" class="qr-code"></canvas>
                            <div class="qr-buttons">
                                <button @click="downloadQR" class="download-btn">Download QR</button>
                                <button @click="printQR" class="print-btn">Print QR</button>
                            </div>
                        </div>
                    </div>

                    <div class="actions" v-if="currentUser">
                        <button @click="saveChanges" class="save-btn">Save Changes</button>
                        <button @click="resetChanges" class="reset-btn">Reset</button>
                        <button v-if="userRole === 'admin'" @click="confirmDelete" class="delete-btn">Delete Device</button>
                    </div>
                    <div v-else class="login-required">
                        <p>Please log in to edit device information</p>
                    </div>
                </div>
            </div>

            <LoadingSkeleton v-if="loading" />
            <div v-if="!loading && !device" class="no-device">
                <p>Device not found</p>
            </div>
        </main>

        <CustomPopup
            :show="popup.show"
            :type="popup.type"
            :title="popup.title"
            :message="popup.message"
            @close="closePopup"
        />

        <CustomPopup
            :show="showDeleteConfirm"
            type="warning"
            title="Delete Device"
            :message="`Are you sure you want to delete device ${device?.id}? This action cannot be undone.`"
            @close="showDeleteConfirm = false"
            @confirm="handleDeleteConfirm"
        />
    </div>
</template>

<script setup>
import {ref, onMounted, computed, nextTick} from "vue";
import {useRoute, useRouter} from "vue-router";
import {auth} from "@/firebase";
import {onAuthStateChanged} from "firebase/auth";
import {getDeviceById, updateDevice, deleteDevice} from "../services/deviceService";
import {getUserRole, getAdminUsers} from "../services/userService";
import {logDeviceEdit, getDeviceHistory} from "../services/editHistoryService";
import {COLORS, FONTS, GRADIENTS} from "@/styles/brand";
import CustomPopup from "./CustomPopup.vue";
import AppHeader from "./AppHeader.vue";
import LoadingSkeleton from "./LoadingSkeleton.vue";
import QRCode from "qrcode";

const route = useRoute();
const router = useRouter();
const device = ref(null);
const editableDevice = ref({});
const loading = ref(true);
const popup = ref({show: false, type: "", title: "", message: ""});
const currentUser = ref(null);
const userRole = ref("user");
const currentUsageHours = ref(0);
const qrCanvas = ref(null);
const adminUsers = ref([]);
const editHistory = ref([]);
const showHistory = ref(false);
const showDeleteConfirm = ref(false);
const backRoute = computed(() => ({ path: '/', query: route.query }));

const formatDate = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp.seconds * 1000).toLocaleString();
};

const saveChanges = async () => {
    if (!currentUser.value) {
        showPopup("error", "Authentication Required", "Please log in to update device information");
        return;
    }

    try {
        const changes = {};
        Object.keys(editableDevice.value).forEach(key => {
            if (editableDevice.value[key] !== device.value[key]) {
                changes[key] = { old: device.value[key], new: editableDevice.value[key] };
            }
        });

        await updateDevice(device.value.id, editableDevice.value);
        
        if (Object.keys(changes).length > 0) {
            await logDeviceEdit(device.value.id, changes, currentUser.value.email);
            editHistory.value = await getDeviceHistory(device.value.id);
        }

        device.value = {...editableDevice.value};
        showPopup("success", "Success", "Device updated successfully!");
    } catch (error) {
        console.error("Error updating device:", error);
        showPopup("error", "Error", "Failed to update device");
    }
};

const showPopup = (type, title, message) => {
    popup.value = {show: true, type, title, message};
};

const closePopup = () => {
    popup.value.show = false;
};

const resetChanges = () => {
    editableDevice.value = {...device.value};
};

const canEditLastTakenBy = computed(() => {
    return userRole.value === "admin";
});

const canSignOff = computed(() => {
    return currentUser.value &&
        device.value?.lastTakenBy === currentUser.value.email &&
        userRole.value !== "admin";
});

const calculateUsageHours = () => {
    if (editableDevice.value.lastTakenAt) {
        const takenTime = editableDevice.value.lastTakenAt.seconds ?
            new Date(editableDevice.value.lastTakenAt.seconds * 1000) :
            new Date(editableDevice.value.lastTakenAt);
        const now = new Date();
        const diffMs = now - takenTime;
        currentUsageHours.value = diffMs / (1000 * 60 * 60); // Convert to hours
    }
};

const signOffDevice = async () => {
    try {
        // Calculate usage hours for this session
        const sessionHours = currentUsageHours.value;
        const newTotalHours = (editableDevice.value.totalHours || 0) + sessionHours;
        const newCheckoutCount = (editableDevice.value.checkoutCount || 0) + 1;

        await updateDevice(device.value.id, {
            lastTakenBy: null,
            lastTakenAt: null,
            totalHours: newTotalHours,
            checkoutCount: newCheckoutCount,
        });

        editableDevice.value.lastTakenBy = null;
        editableDevice.value.lastTakenAt = null;
        editableDevice.value.totalHours = newTotalHours;
        editableDevice.value.checkoutCount = newCheckoutCount;
        currentUsageHours.value = 0;

        showPopup("success", "Success", `Device signed off! Session: ${sessionHours.toFixed(1)}h`);
    } catch (error) {
        console.error("Error signing off device:", error);
        showPopup("error", "Error", "Failed to sign off device");
    }
};

const generateQR = async () => {
    if (device.value && qrCanvas.value) {
        const url = `https://device-tracker.nordeus.com/device-checkout/${device.value.id}`;

        // Create temporary canvas for QR code only
        const tempCanvas = document.createElement("canvas");
        await QRCode.toCanvas(tempCanvas, url, {
            width: 200,
            margin: 2,
            color: {
                dark: "#000000",
                light: "#FFFFFF",
            },
        });

        // Set main canvas size to accommodate logo + top text + QR + bottom text
        qrCanvas.value.width = tempCanvas.width;
        qrCanvas.value.height = tempCanvas.height + 90;

        const ctx = qrCanvas.value.getContext("2d");

        // Add background for top area with curved top corners only
        ctx.fillStyle = COLORS.PRIMARY.RED;
        const topAreaHeight = 30;
        const cornerRadius = 8;

        ctx.beginPath();
        ctx.moveTo(cornerRadius, 0);
        ctx.lineTo(qrCanvas.value.width - cornerRadius, 0);
        ctx.quadraticCurveTo(qrCanvas.value.width, 0, qrCanvas.value.width, cornerRadius);
        ctx.lineTo(qrCanvas.value.width, topAreaHeight);
        ctx.lineTo(0, topAreaHeight);
        ctx.lineTo(0, cornerRadius);
        ctx.quadraticCurveTo(0, 0, cornerRadius, 0);
        ctx.closePath();
        ctx.fill();

        const topLogoImg = new Image();
        topLogoImg.onload = () => {
            const logoWidth = 23;
            const logoHeight = 23;
            const logoX = 15;
            ctx.drawImage(topLogoImg, logoX, 5, logoWidth, logoHeight);
        };
        topLogoImg.src = '/nordeus.ico';

        ctx.fillStyle = COLORS.PRIMARY.WHITE;
        ctx.font = `bold 16px ${FONTS.PRIMARY}`;
        ctx.textAlign = "center";
        ctx.fillText("DEVICE TRACKER", 110, 22);




        // Draw QR code in middle
        ctx.drawImage(tempCanvas, 0, 30);


        // Add black rectangle for device ID
        ctx.fillStyle = '#000000';
        const deviceIdY = tempCanvas.height + 30;
        const deviceIdHeight = 30;
        ctx.fillRect(0, deviceIdY, qrCanvas.value.width, deviceIdHeight);
        
        // Add device ID text
        ctx.fillStyle = COLORS.PRIMARY.WHITE;
        ctx.font = `bold 16px ${FONTS.PRIMARY}`;
        ctx.textAlign = "center";
        ctx.fillText(device.value.id, qrCanvas.value.width / 2, deviceIdY + deviceIdHeight - 8);


        // Add background for bottom text area with curved bottom corners only
        ctx.fillStyle = COLORS.PRIMARY.RED;
        const textY = tempCanvas.height + 60;
        const textHeight = 30;

        ctx.beginPath();
        ctx.moveTo(0, textY);
        ctx.lineTo(qrCanvas.value.width, textY);
        ctx.lineTo(qrCanvas.value.width, textY + textHeight - cornerRadius);
        ctx.quadraticCurveTo(qrCanvas.value.width, textY + textHeight, qrCanvas.value.width - cornerRadius, textY + textHeight);
        ctx.lineTo(cornerRadius, textY + textHeight);
        ctx.quadraticCurveTo(0, textY + textHeight, 0, textY + textHeight - cornerRadius);
        ctx.closePath();
        ctx.fill();

        // Add white text on red background
        ctx.fillStyle = COLORS.PRIMARY.WHITE;
        ctx.font = `bold 16px ${FONTS.PRIMARY}`;
        ctx.textAlign = "center";
        ctx.fillText("SCAN FOR PIN!", qrCanvas.value.width / 2, qrCanvas.value.height - 8);
    }
};

const downloadQR = () => {
    if (qrCanvas.value) {
        const link = document.createElement("a");
        link.download = `${device.value.id}-qr.png`;
        link.href = qrCanvas.value.toDataURL();
        link.click();
    }
};

const printQR = () => {
    if (qrCanvas.value) {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head><title>QR Code - ${device.value.id}</title></head>
                <body style="margin:0; display:flex; justify-content:center; align-items:center; min-height:100vh;">
                    <img src="${qrCanvas.value.toDataURL()}" style="max-width:100%; height:auto;" />
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    }
};

const handleImageError = (e) => {
    e.target.src = '/placeholder-phone.svg'
}

const confirmDelete = () => {
    showDeleteConfirm.value = true
}

const handleDeleteConfirm = async () => {
    showDeleteConfirm.value = false
    try {
        await deleteDevice(device.value.id)
        showPopup('success', 'Success', 'Device deleted successfully')
        setTimeout(() => router.push('/'), 1500)
    } catch (error) {
        showPopup('error', 'Error', 'Failed to delete device')
    }
}

onMounted(async () => {
    const deviceId = route.params.deviceId;
    if (deviceId) {
        device.value = await getDeviceById(deviceId);
        editableDevice.value = {...device.value};
        calculateUsageHours();
        editHistory.value = await getDeviceHistory(deviceId);

        await nextTick();
        generateQR();

        // Update current usage every minute
        setInterval(calculateUsageHours, 60000);
    }

    loading.value = false;

    adminUsers.value = await getAdminUsers();

    onAuthStateChanged(auth, async (user) => {
        if (user && user.email.endsWith("@nordeus.com")) {
            currentUser.value = user;
            userRole.value = await getUserRole(user.email);
        }
    });
});
</script>

<style scoped>
.preview-container {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.8) 0%, rgba(20, 64, 154, 0.8) 50%, rgba(31, 31, 79, 0.9) 100%),
    url('@/assets/nordeus-3840x1080-pattern_new.jpg') no-repeat center center fixed;
    background-size: cover;
    position: relative;
    font-family: v-bind('FONTS.GOOGLE');
}

.main-content {
    flex: 1;
    padding: 40px;
}

.device-preview {
    display: grid;
    grid-template-columns: 400px 1fr;
    gap: 60px;
    max-width: 1200px;
    margin: 0 auto;
    color: v-bind('COLORS.PRIMARY.WHITE');
    align-items: start;
}

.device-image {
    width: 100%;
    max-width: 400px;
    height: 400px;
    object-fit: cover;
    border-radius: 16px;
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.device-info {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    align-content: start;
}

.form-group {
    margin: 0;
    text-align: left;
}

.actions {
    grid-column: 1;
}

.form-group label {
    display: block;
    color: v-bind('COLORS.PRIMARY.WHITE');
    font-weight: 600;
    margin-bottom: 8px;
}

.form-group input, .form-group select, .form-group textarea {
    width: 100%;
    padding: 12px 16px;
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.1);
    color: v-bind('COLORS.PRIMARY.WHITE');
    font-size: 1rem;
    box-sizing: border-box;
    font-family: inherit;
}

.form-group textarea {
    resize: vertical;
    min-height: 80px;
}

.notes-field {
    grid-column: 1 / -1;
}

.form-group input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.sign-off-btn {
    margin-top: 8px;
    padding: 8px 16px;
    background: v-bind('COLORS.PRIMARY.RED');
    color: white;
    border: none;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 0.9rem;
}

.sign-off-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.actions {
    display: flex;
    gap: 16px;
    margin-top: 24px;
    justify-content: flex-start;
}

.save-btn, .reset-btn, .delete-btn {
    padding: 12px 24px;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    white-space: nowrap;
}

.save-btn {
    background: v-bind('COLORS.PRIMARY.RED');
    color: white;
}

.reset-btn {
    background: rgba(255, 255, 255, 0.2);
    color: v-bind('COLORS.PRIMARY.WHITE');
}

.delete-btn {
    background: #000000;
    color: white;
}

.delete-btn:hover {
    background: #333333;
}

.save-btn:hover, .reset-btn:hover {
    transform: translateY(-2px);
}

.login-required {
    grid-column: 1 / -1;
    text-align: center;
    padding: 20px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.login-required p {
    color: rgba(255, 255, 255, 0.8);
    margin: 0;
    font-style: italic;
}

.history-section {
    grid-column: 1 / -1;
    padding: 20px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.history-header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.history-section label {
    color: v-bind('COLORS.PRIMARY.WHITE');
    font-weight: 600;
    margin: 0;
}

.toggle-history-btn {
    padding: 8px 16px;
    background: v-bind('COLORS.SECONDARY.BLUE');
    color: white;
    border: none;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.toggle-history-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.history-list {
    max-height: 300px;
    overflow-y: auto;
}

.history-entry {
    background: rgba(0, 0, 0, 0.2);
    padding: 12px;
    border-radius: 8px;
    margin-bottom: 8px;
}

.history-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
}

.history-user {
    color: v-bind('COLORS.PRIMARY.WHITE');
    font-weight: 600;
}

.history-date {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.9rem;
}

.history-changes {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.9rem;
}

.change-item {
    margin: 4px 0;
}

.qr-section {
    grid-column: 1 / -1;
    text-align: center;
    padding: 20px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.qr-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.qr-code {
    background: white;
    border-radius: 8px;
    padding: 10px;
}

.qr-buttons {
    display: flex;
    gap: 12px;
    justify-content: center;
}

.download-btn,
.print-btn {
    padding: 8px 16px;
    color: white;
    border: none;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.download-btn {
    background: v-bind('COLORS.SECONDARY.GREEN');
}

.print-btn {
    background: v-bind('COLORS.SECONDARY.BLUE');
}

.download-btn:hover,
.print-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}


.no-device {
    background: v-bind('GRADIENTS.CARD');
    backdrop-filter: blur(20px);
    border: 1px solid v-bind('COLORS.PRIMARY.RED + "80"');
    padding: 40px;
    border-radius: 24px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
    text-align: center;
    color: v-bind('COLORS.PRIMARY.WHITE');
}
</style>
